import { useMemo, useRef, useEffect } from "react";
import { useQuery } from "@apollo/client/react";
import {
  Document,
  FetchAiChatMessagesByAiImportIdDocument,
  FetchAiChatMessagesByAiImportIdQuery,
  FetchAiChatMessagesByAiImportIdQueryVariables,
} from "@/graphql/__generated__/graphql";

/**
 * Options for the useChatMessages hook
 */
interface UseChatMessagesOptions {
  /** Array of tender documents available for chat context */
  tenderDocumentsData: Document[];
}

/**
 * Return type for the useChatMessages hook
 */
interface UseChatMessagesReturn {
  /** Array of chat messages from the database */
  messages: FetchAiChatMessagesByAiImportIdQuery["fetchAiChatMessagesByAIImportId"] | undefined;
  /** Whether messages are currently loading (only true on initial load) */
  loading: boolean;
  /** Function to refetch messages from the database */
  refetch: () => Promise<any>;
  /** Whether there are any messages in the database */
  hasMessages: boolean;
  /** The aiImportId extracted from documents */
  aiImportId: number | undefined;
  /** Whether there are no documents available */
  noDocuments: boolean;
}

/**
 * Custom hook for managing chat messages from the database.
 * Handles GraphQL query, refetching, and derived state.
 *
 * @param options - Configuration options including tender documents
 * @returns Message data, loading state, and refetch function
 *
 * @example
 * const { messages, loading, refetch, hasMessages, aiImportId } = useChatMessages({
 *   tenderDocumentsData: documents
 * });
 */
export function useChatMessages({
  tenderDocumentsData,
}: UseChatMessagesOptions): UseChatMessagesReturn {
  const noDocuments = !tenderDocumentsData || tenderDocumentsData.length === 0;

  // Get aiImportId early for consistent use across all operations
  const aiImportId = useMemo(
    () => tenderDocumentsData?.[0]?.aiImportId ?? undefined,
    [tenderDocumentsData]
  );

  // Track if this is the initial load (first time loading messages)
  const isInitialLoadRef = useRef(true);
  const hasLoadedOnceRef = useRef(false);

  // GraphQL query to fetch existing chat messages from database
  const { data: dbMessagesData, loading: dbMessagesLoading, refetch: dbMessagesRefetch } =
    useQuery<
      FetchAiChatMessagesByAiImportIdQuery,
      FetchAiChatMessagesByAiImportIdQueryVariables
    >(FetchAiChatMessagesByAiImportIdDocument, {
      variables: { aiImportId: aiImportId as number },
      fetchPolicy: "cache-and-network", // Always check network for fresh data
      skip: noDocuments, // Skip query if no documents available
      notifyOnNetworkStatusChange: false, // Don't trigger loading state on refetch
    });

  // Track when initial load completes
  useEffect(() => {
    if (!dbMessagesLoading && !hasLoadedOnceRef.current) {
      hasLoadedOnceRef.current = true;
      isInitialLoadRef.current = false;
    }
  }, [dbMessagesLoading]);

  // Reset initial load flag when aiImportId changes (new chat session)
  useEffect(() => {
    if (aiImportId !== undefined) {
      isInitialLoadRef.current = true;
      hasLoadedOnceRef.current = false;
    }
  }, [aiImportId]);

  const messages = dbMessagesData?.fetchAiChatMessagesByAIImportId;
  const hasMessages = (messages?.length ?? 0) > 0;

  // Only show loading state on initial load, not on refetch
  const loading = isInitialLoadRef.current && dbMessagesLoading;

  return {
    messages,
    loading,
    refetch: dbMessagesRefetch,
    hasMessages,
    aiImportId,
    noDocuments,
  };
}
